// Third-party / environment noise filters for Sentry.
//
// Silences errors no code path of ours can fix, without ever masking a
// first-party failure: blanket message patterns are reserved for messages
// our code cannot produce, injected-bridge errors are scoped by frame
// origin instead. Each entry names the Sentry issue it was verified against.

// App-level control-flow rejections the app deliberately ignores (also
// suppressed in main.js's global handlers) — kept here so the Sentry list
// can't drift from that intent.
export const APP_HANDLED_IGNORE_PATTERNS = [
  'Network Error',
  'END_MATCH_CANCELLED'
];

// KL-D6/KL-BJ: ad blockers rejecting gtag.js pings. Only the hosts gtag talks
// to — accounts./pay.google.com carry our own login and payment flows.
const GOOGLE_TAG_FETCH_PATTERNS = [
  /Failed to fetch \((?:www\.)?google\.com\)/,
  /Failed to fetch \((?:[\w-]+\.)?analytics\.google\.com\)/,
  /Failed to fetch \((?:[\w.-]+\.)?(?:doubleclick\.net|googletagmanager\.com|google-analytics\.com|googleadservices\.com|googlesyndication\.com)\)/
];

// Messages with no actionable first-party cause.
export const NOISE_IGNORE_PATTERNS = [
  // KL-19: a browser extension messaging a tab that no longer exists.
  'Invalid call to runtime.sendMessage(). Tab not found.',
  // KL-18: WebGL context creation failure on old devices/drivers. MapLibre
  // throws the raw context-attributes object when the browser provides one,
  // plain text otherwise (the dominant shape in production). Caveat: also
  // hides a hypothetical context-cap leak; accepted until the map gets a
  // fallback.
  '"requestedAttributes"',
  'Failed to initialize WebGL',
  ...GOOGLE_TAG_FETCH_PATTERNS
];

// KL-2M/KL-E4: Facebook/Instagram in-app-browser's injected bridge script.
// URL-scoped (the SDK matches the throwing frame's filename), so failures
// of our own ReactNativeWebView bridge still report.
export const NOISE_DENY_URLS = [/^iabjs:\/\//];

// Errors thrown by scripts injected into in-app browsers. These messages
// CAN also come from first-party code (our SSO buttons call the same
// native bridges), so they are only dropped when the stack carries no
// http(s)-served frame at all — see isInjectedNoiseEvent.
const INJECTED_NOISE_MESSAGES = [
  // KL-13/KL-JX/KL-JR/KL-E7: recursion inside injected iOS scripts
  // (Firefox phrases the same overflow as "too much recursion").
  /Maximum call stack size exceeded/i,
  /too much recursion/i,
  // KL-22: probing the iOS native bridge namespace.
  /evaluating 'window\.webkit\.messageHandlers'/,
  // KL-2M/KL-E4 variants that arrive without an iabjs:// frame.
  'Java object is gone',
  'Java exception was raised during method invocation'
];

function matchesInjectedMessage(message) {
  return INJECTED_NOISE_MESSAGES.some(pattern =>
    typeof pattern === 'string'
      ? message.includes(pattern)
      : pattern.test(message)
  );
}

// Ours runs from a .js file; injected code has no frames or page-URL frames
// (extension-less third-party scripts count as injected too — accepted).
const SCRIPT_FILE_FRAME_PATTERN = /^https?:\/\/[^?#]*\.m?js(?:[?#]|$)/i;

// The thrown error is the LAST exception value: linkedErrors prepends `cause`.
export function isInjectedNoiseEvent(event) {
  const values = event?.exception?.values;
  if (!values || values.length === 0) return false;

  const primary = values[values.length - 1];
  if (!matchesInjectedMessage(primary?.value || '')) return false;

  // Collected with a per-value guard: the SDK's getFramesFromEvent throws
  // internally on any value without a stacktrace and then discards ALL
  // frames, which would drop real errors in mixed cause chains.
  const frames = values.flatMap(
    value => (value.stacktrace && value.stacktrace.frames) || []
  );
  const hasScriptFrame = frames.some(
    frame => frame.filename && SCRIPT_FILE_FRAME_PATTERN.test(frame.filename)
  );
  return !hasScriptFrame;
}

// KL-7: a CSS chunk load error is the only trace WebKit leaves when a document
// is torn down mid lazy-load (<link> fires error, <script> stays silent).
// Stale-deploy failures are handled up front by versionWatch and still surface
// as JS chunk errors, which are not touched here.
export const CSS_CHUNK_REPORT_DELAY_MS = 1000;

let tearingDown = false;
let teardownListeners = null;

// pagehide is what iOS Safari fires reliably; pageshow (bfcache restore)
// re-arms reporting so a restored document is not muted for life.
export function installTeardownTracker() {
  if (teardownListeners) return;

  const mark = () => {
    tearingDown = true;
  };
  const clear = () => {
    tearingDown = false;
  };
  teardownListeners = { mark, clear };
  window.addEventListener('pagehide', mark);
  window.addEventListener('pageshow', clear);
}

export function isCssChunkLoadError(event) {
  const values = event?.exception?.values;
  if (!values || values.length === 0) return false;

  const primary = values[values.length - 1];
  return (
    primary?.type === 'ChunkLoadError' &&
    /^Loading CSS chunk /.test(primary.value || '')
  );
}

// null once the unload has started; otherwise the event, provided the document
// is still alive after the delay — a reloaded document never runs the timer.
export function deferUnlessTearingDown(event) {
  if (tearingDown) return null;

  return new Promise(resolve => {
    setTimeout(
      () => resolve(tearingDown ? null : event),
      CSS_CHUNK_REPORT_DELAY_MS
    );
  });
}

export function __resetTeardownForTests() {
  if (teardownListeners) {
    window.removeEventListener('pagehide', teardownListeners.mark);
    window.removeEventListener('pageshow', teardownListeners.clear);
    teardownListeners = null;
  }
  tearingDown = false;
}
